import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import AdinkraMark from "@/components/AdinkraMark";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Droplet, Volume2, VolumeX, ArrowRight } from "lucide-react";

const STAGES = [
  { t: "Spoken", sym: "nkonsonkonson", note: "A colleague says the difficult thing." },
  { t: "Heard", sym: "akoma", note: "You receive it — or you don't.", leakName: "The Attention Leak", leakNote: "It is said, but never truly received — drowned by the shift, the noise, the next task." },
  { t: "Recorded", sym: "hwehwemudua", note: "It is written down, or lost.", leakName: "The Record Leak", leakNote: "Heard, but never written down — so it cannot travel beyond the person who first heard it." },
  { t: "Interpreted", sym: "nteasee", note: "Meaning is made of it." },
  { t: "Decided", sym: "nkyinkyim", note: "A path is chosen.", leakName: "The Decision Leak", leakNote: "Noted, but no one chooses to act — the truth sits in a system that never decides." },
  { t: "Acted", sym: "nsaa", note: "Something is actually done.", leakName: "The Follow-through Leak", leakNote: "Decided, but nothing actually changes on the ground — the loop is never closed." },
  { t: "Seen", sym: "nsoromma", note: "The change is witnessed." },
];

const N = STAGES.length;
const pct = (i) => 6 + (i * (94 - 6)) / (N - 1);

export default function TruthJourney() {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0); // 0..100 along the path
  const [soundOn, setSoundOn] = useState(false);
  const audioRef = useRef(null);
  const lastPlayedRef = useRef(-1);
  const padRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.85", "end 0.35"] });

  const orbLeft = useTransform(scrollYProgress, [0, 1], ["6%", "94%"]);
  const orbOpacity = useTransform(scrollYProgress, [0, 0.04, 0.96, 1], [0, 1, 1, 0.15]);
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  // Warm inharmonic bell — synthesised, no asset needed.
  const NOTES = [523.25, 587.33, 659.25, 783.99, 880.0, 987.77, 1174.66];
  const chime = (i) => {
    const ctx = audioRef.current;
    if (!ctx) return;
    const now = ctx.currentTime;
    const base = NOTES[Math.max(0, Math.min(NOTES.length - 1, i))];
    const master = ctx.createGain();
    master.gain.value = 0.9;
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 5400;
    master.connect(lp).connect(ctx.destination);
    const partials = [
      [1.0, 0.15, 2.8],
      [2.0, 0.10, 2.3],
      [2.76, 0.07, 1.9],
      [3.52, 0.05, 1.4],
      [5.1, 0.03, 1.0],
    ];
    partials.forEach(([r, g, d], idx) => {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = base * r * (1 + idx * 0.0012);
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(g, now + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + d);
      osc.connect(gain).connect(master);
      osc.start(now);
      osc.stop(now + d + 0.1);
    });
  };

  const toggleSound = () => {
    if (!soundOn) {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!audioRef.current) audioRef.current = new Ctx();
        if (audioRef.current.state === "suspended") audioRef.current.resume();
        lastPlayedRef.current = Math.floor((progress / 100) * (N - 1));
        startPad(audioRef.current);
      } catch (e) { /* ignore */ }
    } else {
      stopPad();
    }
    setSoundOn((s) => !s);
  };

  // A warm, breathing indigo drone — open fifths, slow detune LFO. No asset.
  const startPad = (ctx) => {
    if (padRef.current) return;
    const now = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, now);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 900;
    master.connect(lp).connect(ctx.destination);
    const freqs = [98.0, 146.83, 196.0, 293.66]; // G2 · D3 · G3 · D4
    const stops = [];
    freqs.forEach((f, i) => {
      const osc = ctx.createOscillator();
      osc.type = i < 2 ? "sine" : "triangle";
      osc.frequency.value = f;
      const og = ctx.createGain();
      og.gain.value = i === 0 ? 0.55 : 0.22 / i;
      const lfo = ctx.createOscillator();
      lfo.type = "sine";
      lfo.frequency.value = 0.05 + i * 0.017;
      const lfoG = ctx.createGain();
      lfoG.gain.value = 1.5 + i;
      lfo.connect(lfoG).connect(osc.detune);
      osc.connect(og).connect(master);
      osc.start(now);
      lfo.start(now);
      stops.push(osc, lfo);
    });
    // gentle fade-in
    master.gain.linearRampToValueAtTime(0.03, now + 2.2);
    padRef.current = { master, stops };
  };

  const setPadLevel = (p) => {
    const pad = padRef.current;
    const ctx = audioRef.current;
    if (!pad || !ctx) return;
    const target = 0.02 + (p / 100) * 0.09; // swells as the truth travels
    pad.master.gain.setTargetAtTime(target, ctx.currentTime, 0.5);
  };

  const stopPad = () => {
    const pad = padRef.current;
    const ctx = audioRef.current;
    if (!pad || !ctx) return;
    const now = ctx.currentTime;
    pad.master.gain.cancelScheduledValues(now);
    pad.master.gain.setTargetAtTime(0.0001, now, 0.4);
    pad.stops.forEach((n) => { try { n.stop(now + 2); } catch (e) { /* ignore */ } });
    padRef.current = null;
  };

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const p = v * 100;
    setProgress(p);
    setPadLevel(p);
    let count = -1;
    for (let i = 0; i < N; i++) if (p >= pct(i) - 3) count = i;
    if (soundOn && count > lastPlayedRef.current) {
      for (let i = lastPlayedRef.current + 1; i <= count; i++) chime(i);
    }
    lastPlayedRef.current = count;
  });

  const reached = (i) => progress >= pct(i) - 3;

  useEffect(() => () => { try { stopPad(); audioRef.current && audioRef.current.close(); } catch (e) { /* ignore */ } }, []);

  return (
    <section ref={ref} className="relative bg-purple-deep velvet py-24 sm:py-32 overflow-hidden" data-testid="truth-journey">
      <div
        className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(212,175,55,0.14), transparent 62%)" }}
      />
      <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8">
        <button
          onClick={toggleSound}
          className="absolute top-0 right-5 sm:right-8 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-purple-deep/60 backdrop-blur px-3.5 py-2 text-[11px] uppercase tracking-[0.14em] font-bold text-gold-pale hover:bg-purple-deep transition-colors"
          data-testid="journey-sound-toggle"
          aria-pressed={soundOn}
          title={soundOn ? "Sound on — an ambient bed with a chime at each stage" : "Turn on the ambient sound"}
        >
          {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
          {soundOn ? "Sound on" : "Sound off"}
        </button>
        <div className="max-w-2xl">
          <motion.p initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-[11px] uppercase tracking-[0.24em] text-gold-pale font-bold mb-4">
            The journey of a single truth
          </motion.p>
          <motion.h2 initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.08 }} className="font-heading text-white text-4xl sm:text-5xl leading-[1.08] text-balance">
            Seven stages between a word and a change
          </motion.h2>
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.16 }} className="mt-5 text-white/70 text-[16px] leading-relaxed">
            Scroll, and move the truth forward with your own hand. At every crossing it can dim and drift
            away — hover a gold wisp to name the leak. This whole course is the craft of the crossings.
          </motion.p>
        </div>

        {/* ---- DESKTOP: scroll-driven luminous path ---- */}
        <div className="relative mt-24 hidden md:block" style={{ height: "230px" }}>
          <div className="absolute" style={{ top: "44px", left: "6%", right: "6%" }}>
            <div className="h-[2px] w-full rounded-full bg-white/15" />
            <motion.div
              className="h-[2px] rounded-full -mt-[2px] origin-left"
              style={{ background: "linear-gradient(90deg,#B08A2E,#D4AF37,#E8D48B)", scaleX: lineScale }}
            />
          </div>
          {/* scroll-driven orb of light */}
          <motion.div className="absolute -translate-x-1/2" style={{ top: "38px", left: orbLeft, opacity: orbOpacity }}>
            <span className="block w-4 h-4 rounded-full bg-gold" style={{ boxShadow: "0 0 18px 6px rgba(212,175,55,0.75), 0 0 42px 14px rgba(212,175,55,0.35)" }} />
          </motion.div>

          <TooltipProvider delayDuration={80}>
            {STAGES.map((s, i) => {
              const lit = reached(i);
              return (
                <motion.div
                  key={s.t}
                  className="absolute -translate-x-1/2 flex flex-col items-center text-center"
                  style={{ left: `${pct(i)}%`, top: 0, width: "150px" }}
                  animate={{ opacity: lit ? 1 : 0.42 }}
                  transition={{ duration: 0.4 }}
                >
                  <span className="text-[10px] uppercase tracking-[0.2em] text-gold-pale/70 font-bold mb-3">{String(i + 1).padStart(2, "0")}</span>
                  <span className="relative flex items-center justify-center">
                    {lit && <span className="htt-halo absolute inset-0 rounded-full" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.45), transparent 70%)" }} />}
                    <motion.span
                      className="relative w-16 h-16 rounded-full bg-purple-deep flex items-center justify-center shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
                      animate={{ borderColor: lit ? "rgba(212,175,55,0.85)" : "rgba(212,175,55,0.28)", scale: lit ? 1 : 0.94 }}
                      transition={{ duration: 0.4 }}
                      style={{ borderWidth: "1px", borderStyle: "solid" }}
                    >
                      <AdinkraMark symbol={s.sym} className={lit ? "text-gold" : "text-gold/50"} style={{ width: "30px", height: "30px" }} />
                    </motion.span>
                    {s.leakName && (
                      <>
                        <span className="htt-leak absolute left-1/2 top-full w-1.5 h-1.5 rounded-full bg-gold-pale" style={{ animationDelay: `${i * 0.4}s` }} />
                        <span className="htt-leak absolute left-[40%] top-full w-1 h-1 rounded-full bg-gold" style={{ animationDelay: `${i * 0.4 + 1.2}s` }} />
                      </>
                    )}
                  </span>
                  <span className="mt-4 font-heading text-white text-lg">{s.t}</span>
                  <span className="mt-1 text-[12px] text-white/55 leading-snug">{s.note}</span>
                  {s.leakName && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold text-gold-pale/80 hover:text-gold transition-colors cursor-help" data-testid={`leak-${s.t.toLowerCase()}`}>
                          <Droplet className="w-3 h-3" /> a leak lives here
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[240px] bg-purple-deep border border-gold/40 text-white px-3.5 py-2.5" data-testid={`leak-tip-${s.t.toLowerCase()}`}>
                        <p className="text-gold-pale font-bold text-[11px] uppercase tracking-[0.14em] mb-1">{s.leakName}</p>
                        <p className="text-[12.5px] leading-relaxed text-white/85">{s.leakNote}</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </motion.div>
              );
            })}
          </TooltipProvider>
        </div>

        {/* ---- MOBILE: vertical path ---- */}
        <div className="md:hidden mt-14 relative pl-14">
          <div className="absolute left-[26px] top-2 bottom-2 w-[2px] bg-gradient-to-b from-gold-dark via-gold to-gold-pale/40 rounded-full" />
          <div className="space-y-8">
            {STAGES.map((s, i) => (
              <motion.div key={s.t} className="relative flex items-start gap-4" initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}>
                <span className="absolute -left-14 w-14 h-14 rounded-full bg-purple-deep border border-gold/45 flex items-center justify-center">
                  <AdinkraMark symbol={s.sym} className="text-gold" style={{ width: "26px", height: "26px" }} />
                </span>
                <div className="pt-1">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gold-pale/70 font-bold">{String(i + 1).padStart(2, "0")}</p>
                  <p className="font-heading text-white text-lg leading-tight">{s.t}</p>
                  <p className="text-[13px] text-white/60 mt-0.5">{s.note}</p>
                  {s.leakName && (
                    <p className="mt-1 text-[12px] text-gold-pale/90"><span className="font-bold">{s.leakName}:</span> <span className="text-white/60">{s.leakNote}</span></p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Enrol from the journey */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 sm:mt-20 flex flex-col items-center text-center"
        >
          <p className="text-white/70 text-[15px] max-w-md mb-5">
            Every stage is a place you can practise. Begin your own journey through the seven crossings.
          </p>
          <Link
            to="/join"
            className="group inline-flex items-center gap-2 bg-gold text-purple-deep font-bold rounded-full px-8 py-4 text-base hover:bg-gold-pale transition-colors shadow-[0_0_40px_rgba(212,175,55,0.35)]"
            data-testid="journey-join-btn"
          >
            Begin your own journey — £39
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
